import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StepIcon } from '@sorare/core/src/atoms/icons/StepIcon';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { isType, withFragments } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';
import { Lifes } from '#src/components/series/SeriesBoard/Lifes/index.tsx';
import { useLastVisitedBoardView } from '#src/hooks/useLastVisitedBoardView.ts';

import { PickerCard } from './PickerCard.tsx';
import type {
  BoardPicker_careerBoard,
  boardsWithCareerProSteps_careerBoard,
  careerProSteps_careerBoard,
} from './__generated__/index.graphql.ts';

const BoardIcon = styled.img`
  margin-top: var(--intermediate-unit);
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: var(--half-unit);
  object-fit: contain;

  &.sm {
    margin-top: 0;
    width: var(--triple-unit);
    height: var(--triple-unit);
  }
`;

const Grid = styled(Horizontal).attrs({ gap: 1, center: true })`
  flex-wrap: wrap;
  padding: var(--unit);

  @media ${laptopAndAbove} {
    width: 460px;
    padding: var(--double-unit);
  }
`;

const GridCardImage = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  object-fit: contain;
`;

const StepsWrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  --step-overlap: calc(0.75 * var(--unit));
  transform: rotate(180deg);
  padding-top: var(--step-overlap);
  z-index: revert;
  & > * {
    margin-top: calc(-1 * var(--step-overlap));
  }
`;

type Props = {
  boards: readonly BoardPicker_careerBoard[];
  currentBoardId?: string;
  onSelect: (params: { boardId: string; taskId: string }) => void;
  highlighted?: boolean;
} & Pick<ComponentProps<typeof ShieldButton>, 'orientation' | 'size'>;

const careerProSteps = withFragments(
  <T extends careerProSteps_careerBoard>(
    board: T
  ): Extract<T['steps'][number], { __typename: 'CareerProStep' }>[] =>
    board.steps.filter(
      (
        s
      ): s is Extract<T['steps'][number], { __typename: 'CareerProStep' }> => {
        if (!isType(s, 'CareerProStep')) {
          return false;
        }
        return !!s.myTask?.id;
      }
    ),
  {
    careerBoard: gql`
      fragment careerProSteps_careerBoard on CareerBoard {
        id
        steps {
          id
          ... on CareerProStep {
            id
            myTask {
              id
              aasmState
            }
          }
        }
      }
    ` as TypedDocumentNode<careerProSteps_careerBoard>,
  }
);

export const boardsWithCareerProSteps = withFragments(
  <T extends boardsWithCareerProSteps_careerBoard>(boards: readonly T[]) =>
    boards.filter((b): b is T => careerProSteps(b).length > 0),
  {
    careerBoard: gql`
      fragment boardsWithCareerProSteps_careerBoard on CareerBoard {
        id
        ...careerProSteps_careerBoard
      }
      ${careerProSteps.fragments.careerBoard}
    ` as TypedDocumentNode<boardsWithCareerProSteps_careerBoard>,
  }
);

const hasClaimableTask = (board: BoardPicker_careerBoard) =>
  careerProSteps(board).some(
    s => s.myTask.aasmState === ManagerProgressionTaskState.COMPLETED
  );

export const BoardPicker = ({
  boards,
  currentBoardId,
  onSelect,
  size = 'md',
  highlighted,
  orientation,
}: Props) => {
  const { updateBoardView } = useLastVisitedBoardView();
  const boardsWithSteps = boardsWithCareerProSteps(boards);
  const currentBoard = boardsWithSteps.find(b => b.id === currentBoardId);

  if (boardsWithSteps.length <= 1) {
    return null;
  }

  const handleSelectBoard = (boardId: string, closeDropdown: () => void) => {
    const board = boardsWithSteps.find(b => b.id === boardId);
    if (!board) return;

    const steps = careerProSteps(board);
    const taskId = steps.find(s => {
      const state = s.myTask.aasmState;
      return (
        state === ManagerProgressionTaskState.READY ||
        state === ManagerProgressionTaskState.COMPLETED
      );
    })?.myTask.id;
    if (!taskId) return;

    if (board.title) {
      updateBoardView({ lastVisitedBoardTitle: board.title });
    }
    onSelect({ boardId: board.id, taskId });
    closeDropdown();
  };

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          aria-label={currentBoard?.title ?? undefined}
          size={size}
          icon={
            currentBoard?.iconUrl ? (
              <BoardIcon
                className={size === 'sm' ? 'sm' : undefined}
                src={currentBoard.iconUrl}
                alt=""
                role="presentation"
              />
            ) : undefined
          }
          orientation={orientation}
          borderColor={
            highlighted ? (currentBoard?.color ?? undefined) : undefined
          }
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="Choose League" />
          </PickerHeader>
          <Grid>
            {[...boardsWithSteps].reverse().map(board => (
              <PickerCard
                key={board.id}
                selected={board.id === currentBoard?.id}
                background={board.color ?? undefined}
                topRight={
                  hasClaimableTask(board) ? (
                    <span style={{ color: 'var(--c-green-400)' }}>
                      <FontAwesomeIcon icon={iconGift} size="xs" />
                    </span>
                  ) : undefined
                }
                topLeft={
                  <StepsWrapper>
                    {careerProSteps(board).map(step => (
                      <StepIcon
                        key={step.id}
                        status={step.myTask.aasmState}
                        size={1.5}
                      />
                    ))}
                  </StepsWrapper>
                }
                onClick={() => handleSelectBoard(board.id, closeDropdown)}
              >
                {!!board.iconUrl && (
                  <GridCardImage src={board.iconUrl} alt={board.title ?? ''} />
                )}
                <Horizontal gap={0.5} center>
                  {careerProSteps(board).map(step => {
                    const { aasmState } = step.myTask;
                    const { myTask } = step;
                    const isReady =
                      aasmState === ManagerProgressionTaskState.READY;
                    if (isReady && isType(myTask, 'ThresholdPickerTask')) {
                      return (
                        <Horizontal key={step.id} gap={0.5} center>
                          <Lifes task={myTask} size={1.5} />
                        </Horizontal>
                      );
                    }
                    return null;
                  })}
                </Horizontal>
              </PickerCard>
            ))}
          </Grid>
        </>
      )}
    </Dropdown>
  );
};

BoardPicker.fragments = {
  careerBoard: gql`
    fragment BoardPicker_careerBoard on CareerBoard {
      id
      title
      iconUrl
      color(variant: PRIMARY)
      steps {
        id
        ... on CareerProStep {
          id
          myTask {
            id
            ... on ThresholdPickerTask {
              id
              aasmState
              ...Lifes_thresholdPickerTask
            }
          }
        }
      }
      ...boardsWithCareerProSteps_careerBoard
    }
    ${boardsWithCareerProSteps.fragments.careerBoard}
    ${Lifes.fragments.thresholdPickerTask}
  ` as TypedDocumentNode<BoardPicker_careerBoard>,
};
